import { useState, useEffect, useMemo } from 'react'
import { api, FollowerSummary, FollowerGrowthReport } from '../api/client'
import { useWorkspaceContext } from '../hooks/useWorkspaceContext'
import Tabs from '../components/Tabs'

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'growth', label: 'Growth' },
  { id: 'trends', label: 'Trends' },
  { id: 'detail', label: 'Account Detail' },
  { id: 'history', label: 'History' },
]

const COLORS = ['#1b6ec2', '#27ae60', '#e74c3c', '#f39c12', '#8e44ad', '#2c3e50']

interface TimeSeriesPlatform { platform: string; points: { date: string; followers: number }[] }
interface ConnectionDetail {
  connectionID: number; displayName: string; platformName: string
  followerCount: number; followerSyncedAt?: string; isPaused: boolean
  growth?: { startFollowers: number; endFollowers: number; growthAbsolute: number; growthPercent: number }
  dailyPoints: { date: string; followers: number }[]
}

export default function Followers() {
  const { activeWorkspace } = useWorkspaceContext()
  const wsId = activeWorkspace?.workspaceID ?? 0
  const [tab, setTab] = useState('overview')
  const [summary, setSummary] = useState<FollowerSummary | null>(null)
  const [growth, setGrowth] = useState<FollowerGrowthReport | null>(null)
  const [days, setDays] = useState(30)
  const [loading, setLoading] = useState(true)
  const [timeSeries, setTimeSeries] = useState<TimeSeriesPlatform[]>([])
  const [selectedConnection, setSelectedConnection] = useState<number | null>(null)
  const [connectionDetail, setConnectionDetail] = useState<ConnectionDetail | null>(null)
  const [trendDays, setTrendDays] = useState(90)

  useEffect(() => {
    api.get<TimeSeriesPlatform[]>(`/workspaces/${wsId}/followers/time-series?days=${trendDays}`)
      .then(setTimeSeries).catch(() => setTimeSeries([]))
  }, [trendDays])

  const loadConnectionDetail = async (connId: number) => {
    setSelectedConnection(connId); setTab('detail')
    try { setConnectionDetail(await api.get<ConnectionDetail>(`/workspaces/${wsId}/followers/by-connection/${connId}?days=${days}`)) } catch (err) { console.error('[SMM]', err); setConnectionDetail(null) }
  }

  useEffect(() => {
    setLoading(true)
    Promise.all([
      api.get<FollowerSummary>(`/workspaces/${wsId}/followers/summary`),
      api.get<FollowerGrowthReport>(`/workspaces/${wsId}/followers/growth?days=${days}`),
    ]).then(([s, g]) => { setSummary(s); setGrowth(g) }).catch(err => console.error("[SMM]", err)).finally(() => setLoading(false))
  }, [days])

  const maxFollowers = useMemo(() => {
    if (!summary) return 1
    return Math.max(...summary.connections.map(c => c.followerCount), 1)
  }, [summary])

  if (loading) return <div className="smm-loading">Loading follower data...</div>

  return (
    <div className="smm-followers">
      <h1>Followers</h1>
      <Tabs tabs={TABS} activeTab={tab} onTabChange={setTab} />

      {tab === 'overview' && summary && (
        <>
          <div className="smm-card" style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <div style={{ fontSize: '2.5rem', fontWeight: 700, color: 'var(--smm-primary)' }}>{summary.aggregateFollowers.toLocaleString()}</div>
            <p style={{ color: '#6c757d' }}>Total Followers Across All Platforms</p>
          </div>

          <div className="smm-card">
            <h3>By Platform</h3>
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              {summary.connections.map((c, i) => (
                <div key={c.connectionID} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ width: 120, fontWeight: 500, fontSize: '0.85rem' }}>{c.platformName}</div>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ flex: 1, height: 28, background: '#f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${(c.followerCount / maxFollowers) * 100}%`, background: COLORS[i % COLORS.length], borderRadius: 8, transition: 'width 0.5s ease' }} />
                    </div>
                    <span style={{ fontWeight: 600, minWidth: 80, textAlign: 'right' }}>{c.followerCount.toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
            {summary.connections.map((c, i) => (
              <div key={c.connectionID} className="smm-card" style={{ borderLeft: `4px solid ${COLORS[i % COLORS.length]}`, cursor: 'pointer' }}
                onClick={() => loadConnectionDetail(c.connectionID)}>
                <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>{c.platformName}</div>
                <div style={{ fontWeight: 600 }}>{c.displayName}</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{c.followerCount.toLocaleString()}</div>
                <div style={{ fontSize: '0.75rem', color: '#999' }}>
                  {c.followerSyncedAt ? `Synced ${new Date(c.followerSyncedAt).toLocaleDateString()}` : 'Not yet synced'}
                </div>
                <div style={{ fontSize: '0.7rem', color: 'var(--smm-primary, #1b6ec2)', marginTop: '0.25rem' }}>Click for details</div>
              </div>
            ))}
          </div>
        </>
      )}

      {tab === 'growth' && (
        <div className="smm-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3>Growth</h3>
            <select value={days} onChange={e => setDays(Number(e.target.value))} className="smm-select" style={{ width: 'auto' }}>
              <option value={7}>7 days</option><option value={14}>14 days</option><option value={30}>30 days</option><option value={90}>90 days</option>
            </select>
          </div>
          {growth && growth.platformGrowth.length > 0 ? (
            <>
              <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '1.5rem' }}>
                {growth.platformGrowth.map(g => (
                  <div key={g.platform} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{ width: 100, fontWeight: 500, fontSize: '0.85rem' }}>{g.platform}</div>
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ flex: 1, height: 24, background: '#f0f0f0', borderRadius: 8, overflow: 'hidden', position: 'relative' }}>
                        <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1, background: '#ccc' }} />
                        {g.growthAbsolute !== 0 && (
                          <div style={{
                            position: 'absolute', top: 2, bottom: 2, borderRadius: 6,
                            background: g.growthAbsolute > 0 ? '#27ae60' : '#e74c3c',
                            ...(g.growthAbsolute > 0
                              ? { left: '50%', width: `${Math.min(Math.abs(g.growthPercent) / 2, 50)}%` }
                              : { right: '50%', width: `${Math.min(Math.abs(g.growthPercent) / 2, 50)}%` }),
                          }} />
                        )}
                      </div>
                      <span style={{ fontWeight: 600, minWidth: 80, textAlign: 'right', color: g.growthAbsolute >= 0 ? '#27ae60' : '#e74c3c' }}>
                        {g.growthAbsolute >= 0 ? '+' : ''}{g.growthAbsolute.toLocaleString()} ({g.growthPercent >= 0 ? '+' : ''}{g.growthPercent.toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <table className="smm-table">
                <thead><tr><th>Platform</th><th>Start</th><th>End</th><th>Change</th><th>%</th></tr></thead>
                <tbody>{growth.platformGrowth.map(g => (
                  <tr key={g.platform}><td>{g.platform}</td><td>{g.startFollowers.toLocaleString()}</td><td>{g.endFollowers.toLocaleString()}</td>
                    <td style={{ color: g.growthAbsolute >= 0 ? '#27ae60' : '#e74c3c' }}>{g.growthAbsolute >= 0 ? '+' : ''}{g.growthAbsolute.toLocaleString()}</td>
                    <td style={{ color: g.growthPercent >= 0 ? '#27ae60' : '#e74c3c' }}>{g.growthPercent >= 0 ? '+' : ''}{g.growthPercent.toFixed(1)}%</td></tr>
                ))}</tbody>
              </table>
            </>
          ) : <p className="smm-hint">No growth data available for this period. Connect platforms and wait for follower syncs.</p>}
        </div>
      )}

      {tab === 'trends' && (
        <div className="smm-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0 }}>Follower Growth Over Time</h3>
            <select className="smm-select" style={{ width: 'auto' }} value={trendDays} onChange={e => setTrendDays(Number(e.target.value))}>
              <option value={30}>30 days</option><option value={60}>60 days</option><option value={90}>90 days</option><option value={180}>180 days</option>
            </select>
          </div>
          {timeSeries.length > 0 ? (
            <div>
              {timeSeries.map((ts, i) => {
                const pts = ts.points
                if (pts.length < 2) return (
                  <div key={ts.platform} style={{ marginBottom: '1rem' }}>
                    <strong style={{ color: COLORS[i % COLORS.length] }}>{ts.platform}</strong>
                    <span className="smm-hint"> -- not enough data</span>
                  </div>
                )
                const maxF = Math.max(...pts.map(p => p.followers), 1)
                const minF = Math.min(...pts.map(p => p.followers))
                const range = maxF - minF || 1
                const chartW = 600; const chartH = 80
                const path = pts.map((p, j) => {
                  const x = (j / (pts.length - 1)) * chartW
                  const y = chartH - ((p.followers - minF) / range) * (chartH - 8)
                  return `${j === 0 ? 'M' : 'L'}${x},${y}`
                }).join(' ')
                const areaPath = path + ` L${chartW},${chartH} L0,${chartH} Z`
                const first = pts[0]; const last = pts[pts.length - 1]
                const diff = last.followers - first.followers
                return (
                  <div key={ts.platform} style={{ marginBottom: '1.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                      <strong style={{ color: COLORS[i % COLORS.length] }}>{ts.platform}</strong>
                      <span style={{ color: diff >= 0 ? '#27ae60' : '#e74c3c', fontWeight: 600 }}>
                        {diff >= 0 ? '+' : ''}{diff.toLocaleString()}
                      </span>
                    </div>
                    <svg viewBox={`0 0 ${chartW} ${chartH}`} style={{ width: '100%', maxWidth: chartW, height: chartH, borderRadius: 8, border: '1px solid var(--smm-border, #e0e0e0)' }}>
                      <path d={areaPath} fill={COLORS[i % COLORS.length]} opacity="0.1" />
                      <path d={path} fill="none" stroke={COLORS[i % COLORS.length]} strokeWidth="2" />
                    </svg>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#999' }}>
                      <span>{new Date(first.date).toLocaleDateString()}</span>
                      <span>{last.followers.toLocaleString()} followers</span>
                      <span>{new Date(last.date).toLocaleDateString()}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : <p className="smm-hint">No time-series data. Connect platforms and wait for follower syncs to build up data points.</p>}
        </div>
      )}

      {tab === 'detail' && (
        <div className="smm-card">
          <h3>Account Detail</h3>
          {!selectedConnection && <p className="smm-hint">Select an account from the Overview tab to see per-account stats.</p>}
          {connectionDetail && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <div>
                  <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>{connectionDetail.platformName}</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{connectionDetail.displayName}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--smm-primary, #1b6ec2)' }}>{connectionDetail.followerCount.toLocaleString()}</div>
                  <div className="smm-hint">followers</div>
                </div>
              </div>
              {connectionDetail.growth && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div className="smm-card"><div className="smm-hint">Start</div><div style={{ fontWeight: 600 }}>{connectionDetail.growth.startFollowers.toLocaleString()}</div></div>
                  <div className="smm-card"><div className="smm-hint">End</div><div style={{ fontWeight: 600 }}>{connectionDetail.growth.endFollowers.toLocaleString()}</div></div>
                  <div className="smm-card"><div className="smm-hint">Change</div><div style={{ fontWeight: 600, color: connectionDetail.growth.growthAbsolute >= 0 ? '#27ae60' : '#e74c3c' }}>{connectionDetail.growth.growthAbsolute >= 0 ? '+' : ''}{connectionDetail.growth.growthAbsolute.toLocaleString()}</div></div>
                  <div className="smm-card"><div className="smm-hint">%</div><div style={{ fontWeight: 600, color: connectionDetail.growth.growthPercent >= 0 ? '#27ae60' : '#e74c3c' }}>{connectionDetail.growth.growthPercent >= 0 ? '+' : ''}{connectionDetail.growth.growthPercent.toFixed(1)}%</div></div>
                </div>
              )}
              {connectionDetail.dailyPoints.length >= 2 && (() => {
                const pts = connectionDetail.dailyPoints
                const maxF = Math.max(...pts.map(p => p.followers), 1)
                const minF = Math.min(...pts.map(p => p.followers))
                const range = maxF - minF || 1
                const chartW = 600; const chartH = 100
                const path = pts.map((p, j) => {
                  const x = (j / (pts.length - 1)) * chartW
                  const y = chartH - ((p.followers - minF) / range) * (chartH - 8)
                  return `${j === 0 ? 'M' : 'L'}${x},${y}`
                }).join(' ')
                const areaPath = path + ` L${chartW},${chartH} L0,${chartH} Z`
                return (
                  <div>
                    <h4>Daily History ({pts.length} points)</h4>
                    <svg viewBox={`0 0 ${chartW} ${chartH}`} style={{ width: '100%', maxWidth: chartW, height: chartH, borderRadius: 8, border: '1px solid var(--smm-border, #e0e0e0)' }}>
                      <path d={areaPath} fill="#1b6ec2" opacity="0.1" />
                      <path d={path} fill="none" stroke="#1b6ec2" strokeWidth="2" />
                    </svg>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#999', marginTop: '0.25rem' }}>
                      <span>{new Date(pts[0].date).toLocaleDateString()}</span>
                      <span>{new Date(pts[pts.length - 1].date).toLocaleDateString()}</span>
                    </div>
                  </div>
                )
              })()}
              {connectionDetail.dailyPoints.length < 2 && <p className="smm-hint">Not enough daily snapshots for a chart. Data accumulates over time.</p>}
            </div>
          )}
        </div>
      )}

      {tab === 'history' && (
        <div className="smm-card">
          <h3>Sync History</h3>
          <p className="smm-hint">Follower counts are synced automatically. The latest sync for each connection is shown in the Overview tab.</p>
          {summary && summary.connections.length > 0 ? (
            <table className="smm-table">
              <thead><tr><th>Platform</th><th>Account</th><th>Current Count</th><th>Last Synced</th></tr></thead>
              <tbody>{summary.connections.map(c => (
                <tr key={c.connectionID}><td>{c.platformName}</td><td>{c.displayName}</td><td>{c.followerCount.toLocaleString()}</td>
                  <td>{c.followerSyncedAt ? new Date(c.followerSyncedAt).toLocaleString() : 'Never'}</td></tr>
              ))}</tbody>
            </table>
          ) : <p className="smm-hint">No connections to show sync history for.</p>}
        </div>
      )}
    </div>
  )
}
